import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    List,
    ListItem,
    ListItemAvatar,
    Avatar,
    ListItemText,
    Paper,
    Divider,
    Badge,
    IconButton
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import socket from '../services/socket';
import env from '../config/env';
import Preloader from './Preloader';

const ChatComponent = () => {
    const { user } = useAuth();
    const [users, setUsers] = useState([]); // List of chat users
    const [selectedUser, setSelectedUser] = useState(null); // null = Public/Broadcast
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);

    // Load user list
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const res = await api.get('/chat/users');
                setUsers(res.data);
            } catch (err) {
                console.error('Erro ao buscar usuários do chat:', err);
            }
        };
        fetchUsers();
    }, []);

    // Connect socket and listen
    useEffect(() => {
        if (user?.id) {
            socket.emit('join', user.id);

            socket.on('receive_message', (msg) => {
                // Only add if it belongs to current context (public or selected user)
                setMessages((prev) => [...prev, msg]);
            });

            socket.on('message_sent', (msg) => {
                setMessages((prev) => [...prev, msg]);
            });

            return () => {
                socket.off('receive_message');
                socket.off('message_sent');
            };
        }
    }, [user]);

    // Load messages when switching chat
    useEffect(() => {
        const fetchMessages = async () => {
            setLoading(true);
            try {
                const params = selectedUser ? { contactId: selectedUser.id_usuario } : { contactId: 'public' };
                const res = await api.get('/chat/mensagens', { params });
                setMessages(res.data);
            } catch (err) {
                console.error('Erro ao buscar histórico:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchMessages();
    }, [selectedUser]);

    // Scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    const handleSendMessage = () => {
        if (!newMessage.trim()) return;

        const msgData = {
            remetente_id: user.id,
            destinatario_id: selectedUser ? selectedUser.id_usuario : 'public',
            mensagem: newMessage
        };

        socket.emit('send_message', msgData);
        setNewMessage('');
    };

    const handleUserSelect = (u) => {
        setSelectedUser(u); // u=null implies Public
    };

    // Filter messages for display based on selectedUser
    const displayedMessages = messages.filter(m => {
        if (selectedUser) {
            // Private chat: either Sent by Me to Selected OR Sent by Selected to Me
            return (m.remetente_id === user.id && m.destinatario_id === selectedUser.id_usuario) ||
                (m.remetente_id === selectedUser.id_usuario && m.destinatario_id === user.id);
        } else {
            // Public chat: destinatario_id is null
            return m.destinatario_id === null;
        }
    });


    return (
        <Box sx={{ display: 'flex', height: '100%', border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
            {/* Sidebar: Users */}
            <Box sx={{ width: '30%', borderRight: '1px solid #e0e0e0', bgcolor: '#f5f5f5', overflowY: 'auto' }}>
                <List>
                    {/* Public Chat Option */}
                    <ListItem
                        button
                        selected={selectedUser === null}
                        onClick={() => handleUserSelect(null)}
                    >
                        <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                <GroupIcon />
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText primary="Todos" secondary="Chat Geral" />
                    </ListItem>
                    <Divider />

                    {users.map((u) => (
                        <ListItem
                            button
                            key={u.id_usuario}
                            selected={selectedUser?.id_usuario === u.id_usuario}
                            onClick={() => handleUserSelect(u)}
                        >
                            <ListItemAvatar>
                                <Avatar sx={{ bgcolor: 'primary.main' }}>
                                    {u.st_nome.charAt(0)}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText primary={u.st_nome} secondary={u.en_perfil} />
                        </ListItem>
                    ))}
                </List>
            </Box>

            {/* Main Chat Area */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
                {/* Header */}
                <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        {selectedUser ? selectedUser.st_nome : "Chat Geral (Todos)"}
                    </Typography>
                </Box>

                {/* Messages List */}
                <Box sx={{ flex: 1, p: 2, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Preloader message="Carregando mensagens..." />
                        </Box>
                    ) : (
                        <>
                            {displayedMessages.map((msg, index) => {
                                const isMe = msg.remetente_id === user.id;
                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: isMe ? 'flex-end' : 'flex-start'
                                        }}
                                    >
                                        <Paper
                                            sx={{
                                                p: 1.5,
                                                maxWidth: '70%',
                                                bgcolor: isMe ? '#e3f2fd' : '#f5f5f5',
                                                borderRadius: 2
                                            }}
                                        >
                                            {!isMe && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {msg.remetente?.st_nome || 'Usuário'}
                                                </Typography>
                                            )}
                                            <Typography variant="body1">{msg.mensagem}</Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.5, fontSize: '0.7rem' }}>
                                                {new Date(msg.data_envio || Date.now()).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false })}
                                            </Typography>
                                        </Paper>
                                    </Box>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </>
                    )}
                </Box>

                {/* Input Area */}
                <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 1 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Digite sua mensagem..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    />
                    <IconButton color="primary" onClick={handleSendMessage} disabled={!newMessage.trim()}>
                        <SendIcon />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
};

export default ChatComponent;
